import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Design System
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import deliveryState from './Delivery.state';
import { ANALYTICS_SHOW_COLLECTION_HELP, ANALYTICS_SHOW_PERSONALISATION_HELP } from '@constants/actionConstants';
import scrollConstants from '@constants/scrollConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { initDeliveryPage, resetIsDeliveryPageInitialised } from '@redux/actions/delivery/deliveryActions';
import { isClickAndCollectChoice, isDeliveryChoice } from '@utils/delivery/deliveryHelper';
import setPageTitle from '@utils/helpers/pageTitle';

// Components
import ClickAndCollect from '../click-and-collect';
import BatchedDeliveries from '@components/batched-deliveries';
import Container from '@components/container';
import DeliveryChoice from '@components/delivery/delivery-choice';
import DeliveryAddress from '@components/delivery-address/DeliveryAddress';
import ExpressPayments from '@components/express-payments/ExpressPayments';
import GoToPayment from '@components/go-to-payment';
import MembersWeek from '@components/my-john-lewis/members-week';
import MembershipNotification from '@components/my-john-lewis/myjl-member-notification';
import OrderSummary from '@components/order-summary';
import PartnerDiscount from '@components/partner-discount';
import ScrollAnchor from '@components/scroll-anchor';

const Delivery = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    isDeliveryPageInitialised,
    showCollectionHelp,
    showPersonalisationMessage,
    deliveries,
    isPartnerDiscountProcessing,
    eligibleForPartnerDiscount,
    isGuest,
    isMyJLMember,
    items,
    partnerDiscountStatus,
    selectedDeliveryChoiceId,
    showLoadingSpinner,
    showBatchedDeliveries,
    showEditBasketModal,
  } = useSelector(deliveryState);

  useEffect(() => {
    if (!isDeliveryPageInitialised) {
      // don't init delivery-page when edit basket is open
      if (showEditBasketModal) return;

      dispatch(initDeliveryPage());
    } else dispatch(resetIsDeliveryPageInitialised());

    setPageTitle('Delivery');
  }, []);

  useEffect(() => {
    if (showCollectionHelp) dispatch(triggerAnalyticsEvent(ANALYTICS_SHOW_COLLECTION_HELP));
    if (showPersonalisationMessage) dispatch(triggerAnalyticsEvent(ANALYTICS_SHOW_PERSONALISATION_HELP));
  }, [showCollectionHelp, showPersonalisationMessage]);

  const { sidebarIsVisible } = useWindowSize();

  return (
    <Container maxWidth="md" width="full">
      {isMyJLMember && (
        <>
          {!sidebarIsVisible && <MembershipNotification />}

          {sidebarIsVisible && <MembersWeek sidebarIsVisible={sidebarIsVisible} />}
        </>
      )}

      {eligibleForPartnerDiscount && (
        <PartnerDiscount
          disabled={showLoadingSpinner || isPartnerDiscountProcessing}
          isGuest={isGuest}
          partnerDiscountStatus={partnerDiscountStatus}
        />
      )}

      {!sidebarIsVisible && <OrderSummary collapsed items={items} />}

      <ExpressPayments />

      <DeliveryChoice />

      {isDeliveryChoice(selectedDeliveryChoiceId) && (
        <>
          <ScrollAnchor scrollId={scrollConstants.DELIVERY_CHOICE_SELECT_DELIVERY} />

          <DeliveryAddress disabled={isPartnerDiscountProcessing} />

          {showLoadingSpinner && (
            <Container background="white" flex={{ justifyContent: 'center' }} paddingY="3">
              <LoadingSpinner />
            </Container>
          )}

          {showBatchedDeliveries && (
            <>
              <ScrollAnchor scrollId={scrollConstants.DELIVERY_DETAILS_TOP} />
              <BatchedDeliveries deliveries={deliveries} disabled={isPartnerDiscountProcessing} />
            </>
          )}
        </>
      )}

      {isClickAndCollectChoice(selectedDeliveryChoiceId) && (
        <>
          <ScrollAnchor scrollId={scrollConstants.DELIVERY_CHOICE_SELECT_CLICK_AND_COLLECT} />
          <ClickAndCollect disabled={isPartnerDiscountProcessing} />
        </>
      )}

      <Container background="white" marginTop="2" paddingX="3" paddingY="2">
        <GoToPayment />
      </Container>
    </Container>
  );
};

export default Delivery;
